import React, { useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { generateDateTicks } from "../../../services/dataTicks";
import { SlidersHorizontal } from "lucide-react";

const EmissionOverviewChart = ({ emissionOverviewData }) => {
  console.log("OverviewChart:", emissionOverviewData);

  // Default structure when no data
  const defaultData = {
    dates: [],
    emissions: {
      CO2: { name: "CO₂ Emissions", average: [], min: [], max: [] },
      CH4: { name: "CH₄ (Methane)", average: [], min: [], max: [] },
      NOX: { name: "NOx Emissions", average: [], min: [], max: [] },
      SOX: { name: "SOx Emissions", average: [], min: [], max: [] },
      PM: { name: "Particulate Matter", average: [], min: [], max: [] },
      N20: { name: "N₂O (Nitrous Oxide)", average: [], min: [], max: [] },
      CO2EQUIVALENT: { name: "CO₂ Equivalent", average: [], min: [], max: [] },
      GHG: { name: "GHG Intenisty", average: [], min: [], max: [] },
    },
  };

  const { dates, emissions } = emissionOverviewData || defaultData;

  if (emissions?.GHG) {
    emissions.GHG.name = "GHG Intensity";
  }

  const emissionColors = {
    CO2: "#1a5276", // strong blue
    CH4: "#d35400",
    SOX: "#a93226", // bright orange
    NOX: "#b7950b", // green
    PM: "#566573", // purple
    CO2EQUIVALENT: "#7d3c98",
    N20: "#27ae60",
    GHG: "#117864",
  };

  const [selectedTypes, setSelectedTypes] = useState([
    "CO2",
    "CH4",
    "SOX",
    "NOX",
    "PM",
    "CO2EQUIVALENT",
    "N20",
    "GHG",
  ]);

  const handleToggleType = (type) => {
    setSelectedTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const generateSeries = () => {
    return selectedTypes
      .map((type) => {
        const typeData = emissions[type];
        if (!typeData || !typeData.average || typeData.average.length === 0) {
          return null;
        }

        const fleetData = dates
          .map((date, i) => {
            const dateObj = new Date(date);
            const timestamp = dateObj.getTime();

            // Check if date is valid
            if (isNaN(timestamp)) {
              console.warn(`Invalid date: ${date} for type ${type}`);
              return null;
            }

            return {
              x: timestamp,
              y: typeData.average[i] || 0,
              min: typeData.min[i] || 0,
              max: typeData.max[i] || 0,
              date: dateObj.toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
              }),
              emissionType: typeData.name,
            };
          })
          .filter((point) => point !== null); // Filter out invalid dates

        // Only return series if there's valid data
        if (fleetData.length === 0) {
          return null;
        }

        return {
          name: typeData.name,
          type: "scatter",
          data: fleetData,
          color: emissionColors[type],
          marker: {
            symbol: "circle",
            radius: 6,
            lineWidth: 1,
            lineColor: "#fff",
            states: {
              hover: {
                enabled: true,
                lineWidth: 2,
                radius: 8,
              },
            },
          },
        };
      })
      .filter(Boolean);
  };

  let tickPositions = generateDateTicks(dates);

  const options = {
    chart: {
      type: "scatter",
      plotBorderWidth: 1,
      zooming: {
        type: "xy",
      },
      backgroundColor: "#ffffff",
      height: 480,
      style: {
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      },
    },
    title: {
      text: "Fleet Emissions Analysis",
      align: "left",
      style: {
        color: "#111827",
        fontSize: "18px",
        fontWeight: "600",
        display: "none",
      },
    },
    xAxis: {
      type: "datetime",
      tickPixelInterval: 150,
      // tickPositions: [...new Set(dates.map((d) => new Date(d).getTime()))],
      tickPositions: tickPositions,
      title: {
        text: "Date",
        style: {
          color: "#374151",
          fontWeight: "500",
        },
      },
      labels: {
        format: "{value:%b %d}",
        style: {
          color: "#6b7280",
        },
      },
      gridLineWidth: 1,
      gridLineColor: "#f3f4f6",
    },
    yAxis: {
      title: {
        text: "Emissions (MT/day)",
        style: {
          color: "#374151",
          fontWeight: "500",
        },
      },
      labels: {
        style: {
          color: "#6b7280",
        },
      },
      gridLineWidth: 1,
      gridLineColor: "#f3f4f6",
      minPadding: 0,
      maxPadding: 0.05,
      tickAmount: 6,
    },
    plotOptions: {
      scatter: {
        marker: {
          radius: 6,
          states: {
            hover: {
              enabled: true,
              lineColor: "#fff",
              lineWidth: 2,
              radius: 8,
            },
          },
        },
        states: {
          hover: {
            marker: {
              enabled: true,
            },
          },
        },
      },
    },
    legend: {
      enabled: false,
    },
    tooltip: {
      useHTML: true,
      formatter: function () {
        // Get all series that have data at this x position
        const points = this.series.chart.series.map(series => {
          const point = series.points.find(p => p.x === this.x);
          return point ? {
            color: series.color,
            name: series.name,
            y: point.y,
            emissionType: point.emissionType
          } : null;
        }).filter(point => point !== null);

        // Generate HTML for all points at this x position
        const pointsHTML = points.map(point => {
          const unit = point.emissionType === "GHG Intensity" ? "g CO₂e/MJ" : "MT/day";
          return `
        <div style="display: flex; justify-content: space-between; align-items: center; padding: 2px 0;">
          <div style="display: flex; align-items: center; gap: 6px;">
            <div style="width: 8px; height: 8px; border-radius: 50%; background-color: ${point.color};"></div>
            <span style="font-size: 12px;">${point.name}:</span>
          </div>
          <span style="font-weight: 600; font-size: 12px;">${point.y} ${unit}</span>
        </div>
      `;
        }).join('');

        return `
      <div style="padding: 12px; min-width: 220px;">
        <div style="font-weight: 600; margin-bottom: 8px; color: #374151; border-bottom: 1px solid #e2e8f0; padding-bottom: 6px;">
          ${new Date(this.x).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric"
        })}
        </div>
        <div style="max-height: 200px; overflow-y: auto;">
          ${pointsHTML}
        </div>
      </div>
    `;
      },
      backgroundColor: "rgba(255, 255, 255, 0.98)",
      borderColor: "#e2e8f0",
      borderRadius: 8,
      shadow: true,
      style: {
        fontSize: "12px",
      },
    },
    series: generateSeries(),
    credits: {
      enabled: false,
    },
  };

  return (
    <div className="">
      <div className="flex flex-col gap-4 mb-6">
        <div className="flex items-center gap-4">
          <label className="text-base font-semibold text-gray-700 flex items-center gap-2">
            <SlidersHorizontal className="w-5 h-5 text-[#27ade3]" />
            Select by:
          </label>
          {/* Emission Type Selector */}
          <div className="flex-1 min-w-[200px]">
            <div className="flex flex-wrap gap-3">
              {Object.keys(emissions).map((type) => {
                const typeData = emissions[type];
                const isSelected = selectedTypes.includes(type);

                return (
                  <button
                    key={type}
                    onClick={() => handleToggleType(type)}
                    className={`relative px-2 py-1.5 text-[12px] rounded-lg transition-all duration-200 flex items-center gap-2
                      ${isSelected
                        ? "text-white shadow-md"
                        : "bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200"
                      }
                      overflow-hidden`}
                    style={{
                      backgroundColor: isSelected
                        ? emissionColors[type]
                        : undefined,
                    }}
                  >
                    <span
                      className={`absolute inset-0 bg-white opacity-10 transition-opacity duration-200 ${isSelected ? "opacity-0" : "opacity-0 hover:opacity-10"
                        }`}
                    ></span>
                    <span className="relative z-10">{typeData.name}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      <HighchartsReact highcharts={Highcharts} options={options} />
    </div>
  );
};

export default EmissionOverviewChart;
